import Phaser from 'phaser';

// Процедурный muzzle-flash. Текстуры (3 варианта рваной звезды + 1 круглый
// core) пекутся ОДИН раз в конструкторе через Graphics + generateTexture.
// На fire() спавнятся: звезда (внешнее пламя), core (горячий центр), искры
// burst. Гаснут за ~50-95 мс в зависимости от ствола. Без ассетов.
//
// Блендинг — обычный (default normal). НЕ ADD, потому что центр карты
// светлый: ADD-вспышки на нём бледнеют. Tint + alpha вытягивают цвет.

interface MuzzleStyle {
  // Целевой размер звезды в px (на scaleX=scaleY=1 контейнера).
  size: number;
  // Растяжение вдоль ствола. >1.0 — пламя тянется по линии огня.
  elongation: number;
  // Цвет тинта для звезды (внешнее пламя).
  flameColor: number;
  // Цвет тинта для core (почти-белый горячий центр).
  coreColor: number;
  // Длительность fade-out звезды; core гаснет за durationMs * 0.7.
  durationMs: number;
  // Число искр в одном burst'е.
  sparkCount: number;
}

// Размер baked-текстуры. Реальный отрисованный размер = size/STAR_TEXTURE_SIZE.
const STAR_TEXTURE_SIZE = 64;
const CORE_TEXTURE_SIZE = 32;
// Сколько вариантов «рваной звезды» испечь. Каждый fire() выбирает случайный
// для органичной непредсказуемости силуэта пламени.
const STAR_VARIANTS = 3;
// Случайный наклон звезды относительно угла прицела (±~17°).
const STAR_ANGLE_JITTER_RAD = 0.3;
// Pop-старт scale (0.6 → 1.0) — звезда вспыхивает мгновенно, не растягивается.
const STAR_POP_START = 0.6;
// Разлёт искр ±22° от angle прицела.
const SPARK_CONE_DEG = 22;
const SPARK_GRAVITY_Y = 320;
const SPARK_LIFESPAN_MS = 200;
const SPARK_SPEED_MIN = 80;
const SPARK_SPEED_MAX = 220;
const SPARK_SCALE_START = 0.25;

const DEFAULT_STYLE: MuzzleStyle = {
  size: 28,
  elongation: 1.3,
  flameColor: 0xffb84a,
  coreColor: 0xffffe0,
  durationMs: 70,
  sparkCount: 4,
};

const STYLES: Record<string, MuzzleStyle> = {
  // === ПИСТОЛЕТНАЯ ГРУППА — короткое пламя ===
  // Револьвер — средний размер, тёплое пламя, лёгкая искра.
  revolver: { size: 28, elongation: 1.3, flameColor: 0xffb84a, coreColor: 0xffffe0, durationMs: 70, sparkCount: 4 },
  // Двойной револьвер — то же.
  dual_revolver: { size: 28, elongation: 1.3, flameColor: 0xffb84a, coreColor: 0xffffe0, durationMs: 70, sparkCount: 4 },
  // Узи — мелкий быстрый, бледный (full-auto темп).
  uzi: { size: 22, elongation: 1.4, flameColor: 0xffcd55, coreColor: 0xfffff0, durationMs: 50, sparkCount: 3 },

  // === ТЯЖЁЛОЕ ОРУЖИЕ — крупное пламя, больше искр ===
  // Дробовик — самая крупная вспышка, оранжевая, много искр.
  shotgun: { size: 60, elongation: 1.6, flameColor: 0xffae3a, coreColor: 0xffffd0, durationMs: 95, sparkCount: 10 },
  // Снайперка — большой elongation, дольше горит.
  sniper: { size: 54, elongation: 1.8, flameColor: 0xffc34a, coreColor: 0xffffe8, durationMs: 85, sparkCount: 6 },
  // Штурмовая винтовка.
  assault_rifle: { size: 46, elongation: 1.6, flameColor: 0xffb84a, coreColor: 0xfffff0, durationMs: 65, sparkCount: 5 },
  // Пулемёт — крупная вспышка для full-auto, средняя длительность.
  machinegun: { size: 44, elongation: 1.5, flameColor: 0xffb84a, coreColor: 0xfffff0, durationMs: 60, sparkCount: 5 },
  // Лазерный гатлинг — холодная аура, синее ядро.
  laser: { size: 42, elongation: 1.4, flameColor: 0x88ddff, coreColor: 0xeeffff, durationMs: 60, sparkCount: 4 },
  // Гранатомёт — короткая широкая вспышка (как у пушки), элонгация низкая.
  grenade_launcher: { size: 50, elongation: 1.2, flameColor: 0xff9a3a, coreColor: 0xffeecc, durationMs: 85, sparkCount: 7 },
};

export class MuzzleFlashFX {
  // Глубина отрисовки. Star = depth, core = depth+1, искры = depth-1.
  // Выставляется ИЗВНЕ в GameScene.create() так, чтобы вспышка была выше
  // героя/врагов, но ниже HUD.
  public depth = 0;

  private readonly scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    for (let i = 0; i < STAR_VARIANTS; i++) {
      this.bakeStarTexture(`muzzle_star_${i}`, STAR_TEXTURE_SIZE);
    }
    this.bakeCoreTexture('muzzle_core', CORE_TEXTURE_SIZE);
  }

  // Печёт «рваную звезду»: многолучевая фигура (9 лучей × 2 точки = 18 вершин),
  // внешние точки на jitter'енном outerR, внутренние — на innerR с большим
  // jitter'ом. Заливка polygon'а через beginPath/moveTo/lineTo/fillPath.
  private bakeStarTexture(key: string, size: number): void {
    if (this.scene.textures.exists(key)) return;
    const g = this.scene.add.graphics();
    g.fillStyle(0xffffff, 1);

    const cx = size / 2;
    const cy = size / 2;
    const numRays = 9;
    const outerR = size / 2 - 2;
    const innerR = size / 5;
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < numRays * 2; i++) {
      const a = (i / (numRays * 2)) * Math.PI * 2;
      const isOuter = i % 2 === 0;
      const baseR = isOuter ? outerR : innerR;
      // jitter — внешние ±15%, внутренние ±20% от base.
      const jitter = isOuter ? 0.85 + Math.random() * 0.3 : 0.6 + Math.random() * 0.4;
      const r = baseR * jitter;
      points.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }

    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      g.lineTo(points[i].x, points[i].y);
    }
    g.closePath();
    g.fillPath();
    g.generateTexture(key, size, size);
    g.destroy();
  }

  // Печёт круглый core. Простой fillCircle с padding'ом 2px от края (запас
  // под edge-смягчение фрэймбуфера).
  private bakeCoreTexture(key: string, size: number): void {
    if (this.scene.textures.exists(key)) return;
    const g = this.scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillCircle(size / 2, size / 2, size / 2 - 2);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  // Вспышка в точке (x, y) по углу angle (рад, 0 = вправо). weaponKey
  // выбирает стиль из STYLES, неизвестные ключи → DEFAULT_STYLE.
  fire(x: number, y: number, angle: number, weaponKey: string): void {
    const style = STYLES[weaponKey] ?? DEFAULT_STYLE;

    // === STAR (внешнее пламя) ===
    const starKey = `muzzle_star_${Math.floor(Math.random() * STAR_VARIANTS)}`;
    const starAngle = angle + (Math.random() - 0.5) * STAR_ANGLE_JITTER_RAD;
    const finalScaleX = (style.size / STAR_TEXTURE_SIZE) * style.elongation;
    const finalScaleY = style.size / STAR_TEXTURE_SIZE;

    const star = this.scene.add
      .image(x, y, starKey)
      .setTint(style.flameColor)
      .setRotation(starAngle)
      .setScale(finalScaleX * STAR_POP_START, finalScaleY * STAR_POP_START)
      .setDepth(this.depth);

    this.scene.tweens.add({
      targets: star,
      scaleX: finalScaleX,
      scaleY: finalScaleY,
      alpha: 0,
      duration: style.durationMs,
      ease: 'Quad.easeOut',
      onComplete: () => star.destroy(),
    });

    // === CORE (горячий центр, поверх звезды, гаснет быстрее) ===
    const coreScale = (style.size * 0.5) / CORE_TEXTURE_SIZE;
    const core = this.scene.add
      .image(x, y, 'muzzle_core')
      .setTint(style.coreColor)
      .setScale(coreScale * STAR_POP_START)
      .setDepth(this.depth + 1);

    this.scene.tweens.add({
      targets: core,
      scaleX: coreScale,
      scaleY: coreScale,
      alpha: 0,
      duration: Math.round(style.durationMs * 0.7),
      ease: 'Quad.easeOut',
      onComplete: () => core.destroy(),
    });

    // === SPARKS (burst по углу ствола ±cone, с гравитацией вниз) ===
    const angleDeg = Phaser.Math.RadToDeg(angle);
    const emitter = this.scene.add.particles(x, y, 'muzzle_core', {
      speed: { min: SPARK_SPEED_MIN, max: SPARK_SPEED_MAX },
      angle: { min: angleDeg - SPARK_CONE_DEG, max: angleDeg + SPARK_CONE_DEG },
      scale: { start: SPARK_SCALE_START, end: 0 },
      tint: style.coreColor,
      lifespan: SPARK_LIFESPAN_MS,
      gravityY: SPARK_GRAVITY_Y,
      quantity: style.sparkCount,
      emitting: false,
    });
    emitter.setDepth(this.depth - 1);
    emitter.explode(style.sparkCount);
    // Cleanup после lifespan — emitter уже выпустил всё, GC можно вернуть.
    this.scene.time.delayedCall(SPARK_LIFESPAN_MS * 2, () => emitter.destroy());
  }
}
